/**
 * TemplatesSidebar Component
 *
 * Sidebar navigation for templates page showing:
 * - User's template list (draggable for reordering with @dnd-kit)
 * - Actions to create new templates
 * - Link to template builder
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  LibraryBooks as LibraryIcon,
} from '@mui/icons-material';
import { Box, Button, Paper, Skeleton, Typography, useTheme } from '@mui/material';

import { LAYOUT_DIMENSIONS } from '../constants/layout';
import type { TemplateWithMeta } from '../types';

import { TemplateListItem } from './TemplateListItem';

interface TemplatesSidebarProps {
  /** List of templates */
  templates: TemplateWithMeta[];
  /** Currently selected template ID */
  selectedTemplateId: string | null;
  /** Whether templates are loading */
  isLoading: boolean;
  /** Whether user has custom modules */
  hasCustomModules: boolean;
  /** Callback when template is selected */
  onSelectTemplate: (template: TemplateWithMeta) => void;
  /** Callback when templates are reordered */
  onReorderTemplates: (startIndex: number, endIndex: number) => void;
  /** Callback to create template from scratch */
  onCreateFromScratch: () => void;
  /** Callback to browse library */
  onBrowseLibrary: () => void;
}

export const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({
  templates,
  selectedTemplateId,
  isLoading,
  hasCustomModules,
  onSelectTemplate,
  onReorderTemplates,
  onCreateFromScratch,
  onBrowseLibrary,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeId, setActiveId] = useState<string | null>(null);
  const templatesListRef = useRef<HTMLDivElement>(null);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  // Check if there's more content to scroll
  const checkScrollOverflow = useCallback(() => {
    if (!templatesListRef.current) return;
    const element = templatesListRef.current;
    const hasOverflow = element.scrollHeight > element.clientHeight;
    const isScrolledToBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 5;
    setShowBottomGradient(hasOverflow && !isScrolledToBottom);
  }, []);

  useEffect(() => {
    const element = templatesListRef.current;
    if (!element) return;

    // Check on scroll
    const handleScroll = () => {
      checkScrollOverflow();
    };

    element.addEventListener('scroll', handleScroll, { passive: true });

    // Check on resize
    const resizeObserver = new ResizeObserver(() => {
      checkScrollOverflow();
    });

    resizeObserver.observe(element);

    // Initial check - use requestAnimationFrame to ensure DOM is fully rendered
    const rafId = requestAnimationFrame(() => {
      checkScrollOverflow();
      // Also check again after a short delay to catch any async rendering
      setTimeout(checkScrollOverflow, 100);
    });

    return () => {
      cancelAnimationFrame(rafId);
      element.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [checkScrollOverflow, templates, isLoading]);

  // Configure drag sensors - require 8px drag before activating
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = templates.findIndex(t => t.template_id === active.id);
    const newIndex = templates.findIndex(t => t.template_id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderTemplates(oldIndex, newIndex);
    }
  };

  const handleUploadClick = () => {
    navigate('/templatebuilder');
  };

  const activeTemplate = activeId ? templates.find(t => t.template_id === activeId) : null;

  return (
    <Box
      component="aside"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: LAYOUT_DIMENSIONS.SIDEBAR_WIDTH,
        minWidth: LAYOUT_DIMENSIONS.SIDEBAR_MIN_WIDTH,
        height: '100%',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          height: LAYOUT_DIMENSIONS.TOP_BAR_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="text.primary"
          sx={{ mb: 0.5, lineHeight: 1.2 }}
        >
          Your Templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          Select a template to edit or create a new one
        </Typography>
      </Box>

      {/* Templates List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          minHeight: 0,
        }}
      >
        <Box
          ref={templatesListRef}
          sx={{
            height: '100%',
            overflowY: 'scroll',
            overflowX: 'hidden',
            p: 2,
            pb: 0,
          }}
        >
          {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={48}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : templates.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No templates yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create one below!
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.BeforeDragging,
              },
            }}
          >
            <SortableContext
              items={templates.map(t => t.template_id)}
              strategy={verticalListSortingStrategy}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  pb: 2,
                }}
              >
                {templates.map(template => (
                  <TemplateListItem
                    key={template.template_id}
                    template={template}
                    isSelected={selectedTemplateId === template.template_id}
                    onClick={() => onSelectTemplate(template)}
                  />
                ))}
              </Box>
            </SortableContext>

            {/* Drag Overlay for smooth dragging experience */}
            <DragOverlay dropAnimation={null}>
              {activeTemplate && (
                <Paper
                  elevation={8}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    cursor: 'grabbing',
                    opacity: 0.9,
                    border: 2,
                    borderColor: 'primary.main',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      flex: 1,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {activeTemplate.display_name}
                  </Typography>
                </Paper>
              )}
            </DragOverlay>
          </DndContext>
        )}
        </Box>

        {/* Bottom gradient indicator - fixed to bottom of container */}
        {showBottomGradient && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 32,
              pointerEvents: 'none',
              background: t =>
                `linear-gradient(to bottom, transparent 0%, ${t.palette.grey[100]} 100%)`,
              zIndex: 1,
            }}
          />
        )}
      </Box>

      {/* Actions Section */}
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 1.5,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Create New
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Upload Template Button */}
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleUploadClick}
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 500,
              py: 1,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            Upload Template
          </Button>

          {/* Start from Scratch Button */}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onCreateFromScratch}
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 500,
              py: 1,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            Start from Scratch
          </Button>

          {/* Browse Library Button */}
          <Button
            variant="outlined"
            startIcon={<LibraryIcon />}
            onClick={onBrowseLibrary}
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 500,
              py: 1,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            Browse Library
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
